import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { ServiceAdviceBlock } from "@/components/ServiceAdviceBlock";
import { ServiceCautionBlock } from "@/components/ServiceCautionBlock";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { VEHICLE_FAQ_CATEGORIES } from "@/content/vehicle-corpus/faq-defs";

const ADVICE_TH = [
  "ตรวจเอกสารที่สำนักงานขนส่งพื้นที่นั้นรับก่อนเดินทางไปยื่น เพราะรูปแบบหลักฐานที่อยู่ของชาวต่างชาติแตกต่างกันในแต่ละจังหวัด",
  "แจ้งการโอนรถต่อนายทะเบียนภายในสิบห้าวันนับแต่วันโอนตามกฎหมายว่าด้วยรถยนต์ เพื่อไม่ให้ถูกปรับและไม่ให้ใบสั่งของผู้ซื้อย้อนมาถึงผู้ขาย",
  "ต่อประกันภัยตามพระราชบัญญัติคุ้มครองผู้ประสบภัยจากรถให้คลุมทั้งปีภาษีก่อนไปชำระภาษีรถประจำปีทุกครั้ง",
  "ถือใบขับขี่ให้ตรงประเภทรถที่ขับจริง โดยเฉพาะรถจักรยานยนต์ เพราะเป็นข้อยกเว้นความคุ้มครองที่พบบ่อยที่สุดในกรมธรรม์ภาคสมัครใจ",
  "ตรวจสถานะภาษีค้างและใบสั่งค้างชำระของรถผ่านช่องทางของกรมการขนส่งทางบกก่อนตัดสินใจซื้อรถมือสอง",
  "ทำหนังสือมอบอำนาจโดยระบุกิจการ หมายเลขทะเบียนรถ และเลขตัวถังให้ชัดเจน พร้อมสำเนาเอกสารพิสูจน์ตัวบุคคลที่รับรองสำเนาถูกต้อง",
  "ขอบันทึกประจำวันจากพนักงานสอบสวนทุกครั้งที่เกิดอุบัติเหตุ และขอฉบับคัดสำเนาไว้ล่วงหน้าหากต้องนำไปใช้ต่างประเทศ",
  "ตรวจใบอนุญาตนำเข้าและภาระภาษีอากรกับกรมศุลกากรให้จบก่อนจองระวางเรือนำรถเข้าประเทศ",
];

const CAUTIONS_TH = [
  "เข้าใจว่าใบขับขี่รถยนต์ใช้ขับรถจักรยานยนต์ได้ ทั้งที่กฎหมายแยกใบอนุญาตออกจากกัน",
  "ใช้ใบขับขี่สากลที่ไม่มีสัญลักษณ์ประเภทรถจักรยานยนต์ไปเช่ามอเตอร์ไซค์",
  "ปล่อยให้ร้านเช่ารถยึดหนังสือเดินทางตัวจริงไว้เป็นหลักประกัน",
  "ปล่อยภาษีรถขาดต่อเนื่องเกินสามปีจนทะเบียนถูกระงับและต้องจดทะเบียนใหม่",
  "ซื้อขายรถกันเองโดยไม่ไปแจ้งโอนที่สำนักงานขนส่ง ทำให้ความรับผิดยังผูกกับชื่อในเล่มทะเบียน",
  "จองเรือนำรถเข้าประเทศก่อนได้รับใบอนุญาตนำเข้า ทำให้รถติดค้างและมีค่าใช้จ่ายเก็บรักษา",
  "ใช้คำแปลใบขับขี่ที่สะกดชื่อไม่ตรงกับหนังสือเดินทางโดยไม่มีหนังสือรับรองบุคคลเดียวกัน",
  "รอผลพิจารณาของบริษัทประกันจนใกล้ครบอายุความหนึ่งปีของมูลละเมิดตาม ป.พ.พ. มาตรา 448",
];

export const Route = createFileRoute("/vehicle/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/vehicle-corpus");
    return { faqs: m.VEHICLE_FAQ_TH };
  },
  component: VehicleFaqHub,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `ทะเบียนรถ ใบขับขี่ และงานขนส่งสำหรับต่างชาติ — FAQ ${faqs.length} ข้อ`,
        description:
          "จดทะเบียนและโอนรถในชื่อชาวต่างชาติ ใบรับรองถิ่นที่อยู่ เปลี่ยนใบขับขี่ต่างประเทศเป็นใบขับขี่ไทย ใบขับขี่สากล นำเข้า-ส่งออกรถ ต่อภาษีและ พ.ร.บ. ใบสั่งจราจร และกฎหมายขนส่งทางบก สอบถามทีมงานได้ทุกช่องทาง",
        path: "/vehicle/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/vehicle/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "FAQ ทะเบียนรถและใบขับขี่", url: `${site}/vehicle/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/vehicle/faq")) },
      ],
    };
  },
});

function VehicleFaqHub() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>FAQ ทะเบียนรถและใบขับขี่</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          ทะเบียนรถ ใบขับขี่ และงานขนส่งสำหรับชาวต่างชาติในไทย: จดทะเบียน โอน นำเข้า ต่อภาษี และเอกสารรับรอง
        </h1>
        <p className="mb-6 text-muted-foreground" data-speakable="true">
          รวม {faqs.length} คำตอบอ้างอิงกรมการขนส่งทางบก (พ.ร.บ.รถยนต์ พ.ศ. 2522 และ พ.ร.บ.การขนส่งทางบก พ.ศ. 2522),
          กรมศุลกากร, พ.ร.บ.จราจรทางบก พ.ศ. 2522, พ.ร.บ.คุ้มครองผู้ประสบภัยจากรถ พ.ศ. 2535
          และอนุสัญญาการจราจรทางถนนเจนีวา ค.ศ. 1949 กับเวียนนา ค.ศ. 1968
        </p>

        <section aria-label="หมวดคำถาม" className="mb-10">
          <h2 className="mb-3 text-xl font-bold">เลือกอ่านตามหมวด</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {VEHICLE_FAQ_CATEGORIES.map((c) => (
              <li key={c.slug} className="rounded-lg border bg-card p-4">
                <a href={`/vehicle/faq/${c.slug}`} className="font-semibold hover:underline">{c.titleTh}</a>
                <p className="mt-1 text-sm text-muted-foreground">{c.descTh}</p>
              </li>
            ))}
          </ul>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <ServiceAdviceBlock items={ADVICE_TH} locale="th" />
        <ServiceCautionBlock items={CAUTIONS_TH} locale="th" />

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ให้ทีมงานตรวจชุดเอกสารทะเบียนรถและใบขับขี่ให้ก่อนยื่น</h2>
          <p className="text-sm text-muted-foreground">
            แจ้งประเภทงาน จังหวัดที่รถจดทะเบียน และประเทศที่ออกใบขับขี่หรือเอกสารกรรมสิทธิ์
            สอบถามได้ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อทีมงาน</a>
        </section>
        <FaqSourceNote topic="vehicle" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}